import { useTheme } from '@emotion/react';
import { Diamond, Lock, TipsOne } from '@icon-park/react';
import { Fragment, memo } from 'react';
import { FormattedMessage } from 'react-intl';

import type { WALLET_REFILL_MODES } from '@didaskill/domain/configurations/constants';
import { WALLET_AUTO_REFILL_AMOUNT } from '@didaskill/domain/wallet/constants';
import { Alert, ICON_SIZES, Typography } from '@didaskill/ui/design-system';
import { ComponentSwitch } from '@didaskill/ui/utils';

import messages from './WalletInfo.messages';
import * as styles from './WalletInfo.styles';

type WalletInfoProps = {
  refillMode: WALLET_REFILL_MODES;
  isEligibleToRefill: boolean;
  daysUntilRefill?: number;
  remainingDaysBeforeFullAccess?: number;
};

const WalletInfoBase = ({
  refillMode,
  isEligibleToRefill,
  daysUntilRefill,
  remainingDaysBeforeFullAccess,
}: WalletInfoProps) => {
  const { colors } = useTheme();

  return (
    <Alert withIcon={false} css={styles.container}>
      <ComponentSwitch value={refillMode}>
        <ComponentSwitch.Item value="PERIODICALLY">
          <div css={styles.alertContent}>
            {isEligibleToRefill ? (
              <Fragment>
                <Diamond fill={[colors.primary[500]]} size={ICON_SIZES.LARGE} />

                <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>
                  <FormattedMessage
                    {...messages.DEFAULT_BANNER_CONTENT}
                    values={{
                      monthlyCredits: WALLET_AUTO_REFILL_AMOUNT,
                      daysUntilRefill,
                      b: (chunks) => (
                        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
                          {chunks}
                        </Typography>
                      ),
                    }}
                  />
                </Typography>
              </Fragment>
            ) : (
              <Fragment>
                <TipsOne fill={[colors.primary[500], colors.primary[50]]} size={ICON_SIZES.LARGE} />

                <FormattedMessage {...messages.DEFAULT_NOT_ELIGIBLE_BANNER_CONTENT} />
              </Fragment>
            )}
          </div>
        </ComponentSwitch.Item>

        <ComponentSwitch.Item value="AT_REGISTRATION">
          <div css={styles.alertContent}>
            <Lock fill={[colors.primary[500], colors.primary[50]]} size={ICON_SIZES.LARGE} />

            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>
              <FormattedMessage
                {...messages.DISCOVERY_BANNER_CONTENT}
                values={{
                  days: remainingDaysBeforeFullAccess,
                  b: (chunks) => (
                    <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>{chunks}</Typography>
                  ),
                }}
              />
            </Typography>
          </div>
        </ComponentSwitch.Item>
      </ComponentSwitch>
    </Alert>
  );
};

WalletInfoBase.displayName = 'WalletInfo';

export const WalletInfo = memo(WalletInfoBase);
